class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.onButtonClick = this.onButtonClick.bind(this);
    this.onSliderPaused = this.onSliderPaused.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerCancel.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = () => this.setInteractionPaused(false);
    this.onMouseEnter = () => this.setInteractionPaused(true);
    this.onMouseLeave = () => this.setInteractionPaused(false);
    this.onFocusIn = () => this.setInteractionPaused(true);
    this.onFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.setInteractionPaused(false);
    };
  }

  connectedCallback() {
    if (this.announcementBarConnected) return;

    this.announcementBarConnected = true;
    this.initFrame = requestAnimationFrame(() => this.init());
  }

  disconnectedCallback() {
    this.announcementBarConnected = false;
    cancelAnimationFrame(this.initFrame);
    clearTimeout(this.initRetryTimer);
    this.unload();
  }

  init(attempt = 0) {
    if (!this.announcementBarConnected || !this.isConnected) return;

    this.slider = this.querySelector('.announcement-slider');
    this.slides = Array.from(this.querySelectorAll('.announcement-slider__slide'));
    if (!this.slider || this.slides.length === 0) {
      if (attempt < 20) {
        this.initRetryTimer = setTimeout(() => this.init(attempt + 1), 50);
      }
      return;
    }

    this.currentIndex = 0;
    this.duration = Math.max(1, parseInt(this.dataset.autorotateSpeed, 10) || 5);
    this.forcePaused = this.dataset.autorotate !== 'true';
    this.interactionPaused = false;
    this.isInViewport = true;

    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.playButton = this.querySelector('button[name="play"]');

    this.prevButton && this.prevButton.addEventListener('click', this.onButtonClick);
    this.nextButton && this.nextButton.addEventListener('click', this.onButtonClick);
    this.playButton && this.playButton.addEventListener('click', this.onButtonClick);
    this.addEventListener('slider:paused', this.onSliderPaused);
    this.slider.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    this.slider.addEventListener('pointerup', this.onPointerUp, { passive: true });
    this.slider.addEventListener('pointercancel', this.onPointerCancel, { passive: true });
    this.addEventListener('mouseenter', this.onMouseEnter);
    this.addEventListener('mouseleave', this.onMouseLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        this.isInViewport = entries[0].isIntersecting;
        this.updateAutoplay();
      });
      this.intersectionObserver.observe(this);
    }

    this.renderSlides();
    this.setAttribute('data-slider-ready', 'true');
    this.updateAutoplay();
  }

  unload() {
    clearTimeout(this.autoplayTimer);
    this.prevButton && this.prevButton.removeEventListener('click', this.onButtonClick);
    this.nextButton && this.nextButton.removeEventListener('click', this.onButtonClick);
    this.playButton && this.playButton.removeEventListener('click', this.onButtonClick);
    this.removeEventListener('slider:paused', this.onSliderPaused);
    this.slider && this.slider.removeEventListener('pointerdown', this.onPointerDown);
    this.slider && this.slider.removeEventListener('pointerup', this.onPointerUp);
    this.slider && this.slider.removeEventListener('pointercancel', this.onPointerCancel);
    this.removeEventListener('mouseenter', this.onMouseEnter);
    this.removeEventListener('mouseleave', this.onMouseLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.intersectionObserver && this.intersectionObserver.disconnect();
    this.removeAttribute('data-autoplay-active');
    this.removeAttribute('data-slider-ready');
  }

  onSliderPaused(event) {
    this.setAttribute('data-paused', event.detail.paused);
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.currentTarget;
    
    if (target.name === 'next') {
      this.select(this.currentIndex + 1);
    }
    else if (target.name === 'previous') {
      this.select(this.currentIndex - 1);
    }
    else {
      this.setForcePaused(!this.forcePaused);
    }
  }

  setForcePaused(paused) {
    this.forcePaused = paused;
    this.updateAutoplay();
  }

  setInteractionPaused(paused) {
    this.interactionPaused = paused;
    this.updateAutoplay();
  }

  setPaused(paused) {
    const event = new CustomEvent('slider:paused', {
      detail: { paused }
    });
    this.dispatchEvent(event);
  }

  updateAutoplay() {
    clearTimeout(this.autoplayTimer);
    this.removeAttribute('data-autoplay-active');
    this.style.setProperty('--progress-width', '0%');
    const paused = this.forcePaused
      || this.interactionPaused
      || !this.isInViewport
      || document.hidden
      || this.slides.length <= 1;

    this.setPaused(paused);
    if (paused) return;

    // Force a reflow so the navigation progress animation restarts cleanly.
    void this.offsetWidth;
    this.style.setProperty('--announcement-rotation-duration', `${this.duration}s`);
    this.setAttribute('data-autoplay-active', 'true');
    this.autoplayTimer = setTimeout(() => this.select(this.currentIndex + 1), this.duration * 1000);
  }

  select(index, restartAutoplay = true) {
    if (this.slides.length <= 1) return;

    const nextIndex = (index + this.slides.length) % this.slides.length;
    if (nextIndex === this.currentIndex) return;

    const direction = index < this.currentIndex ? -1 : 1;
    const outgoingSlide = this.slides[this.currentIndex];
    const incomingSlide = this.slides[nextIndex];

    this.slider.classList.remove('announcement-slider--animate');
    this.slides.forEach((slide) => slide.classList.remove('is-before'));
    outgoingSlide.classList.add('is-active');
    incomingSlide.classList.toggle('is-before', direction < 0);

    // Commit the incoming slide's off-screen starting position before moving it.
    void this.slider.offsetWidth;
    this.slider.classList.add('announcement-slider--animate');
    outgoingSlide.classList.remove('is-active');
    outgoingSlide.classList.toggle('is-before', direction > 0);
    incomingSlide.classList.remove('is-before');
    incomingSlide.classList.add('is-active');

    this.currentIndex = nextIndex;
    this.updateSlideAccessibility();
    if (restartAutoplay) this.updateAutoplay();
  }

  renderSlides() {
    this.slider.classList.remove('announcement-slider--animate');
    this.slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === this.currentIndex);
      slide.classList.remove('is-before');
    });
    this.updateSlideAccessibility();
  }

  updateSlideAccessibility() {
    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentIndex;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      slide.querySelectorAll('a, button').forEach((element) => {
        if (isActive) element.removeAttribute('tabindex');
        else element.setAttribute('tabindex', '-1');
      });
    });
  }

  onPointerDown(event) {
    this.pointerStartX = event.clientX;
    this.setInteractionPaused(true);
  }

  onPointerUp(event) {
    if (typeof this.pointerStartX !== 'number') {
      this.setInteractionPaused(false);
      return;
    }

    const distance = event.clientX - this.pointerStartX;
    this.pointerStartX = null;
    if (Math.abs(distance) >= 40) {
      this.select(this.currentIndex + (distance < 0 ? 1 : -1));
    }
    this.setInteractionPaused(false);
  }

  onPointerCancel() {
    this.pointerStartX = null;
    this.setInteractionPaused(false);
  }

  onVisibilityChange() {
    this.updateAutoplay();
  }

  onBlockSelect(event) {
    if (!(event.target instanceof Element)) return;

    const selectedSlide = event.target.closest('.announcement-slider__slide');
    const selectedIndex = this.slides.indexOf(selectedSlide);
    if (selectedIndex < 0) return;

    this.select(selectedIndex, false);
    this.setInteractionPaused(true);
  }
}

class AnnouncementCountdown extends HTMLElement {
  connectedCallback() {
    clearInterval(this.interval);

    this.durationSeconds = Math.max(1, parseInt(this.dataset.durationSeconds, 10) || 2700);
    this.serverNowSeconds = parseInt(this.dataset.serverNow, 10) || Math.floor(Date.now() / 1000);
    this.clientStartedAt = Date.now();
    this.lastRemaining = null;
    this.units = {
      days: this.querySelector('[data-countdown-days]'),
      hours: this.querySelector('[data-countdown-hours]'),
      minutes: this.querySelector('[data-countdown-minutes]'),
      seconds: this.querySelector('[data-countdown-seconds]'),
    };

    this.update();
    this.interval = window.setInterval(() => this.update(), 250);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  update() {
    // The Shopify server epoch is the shared anchor; local storage is never
    // used, so every visitor follows the same repeating global cycle.
    const elapsedSeconds = Math.floor((Date.now() - this.clientStartedAt) / 1000);
    const globalSeconds = this.serverNowSeconds + elapsedSeconds;
    const cycleSeconds = this.durationSeconds + 1;
    const remaining = this.durationSeconds - (globalSeconds % cycleSeconds);

    if (remaining === this.lastRemaining) return;
    this.lastRemaining = remaining;

    const days = Math.floor(remaining / 86400);
    const hours = Math.floor((remaining % 86400) / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    const seconds = remaining % 60;
    const pad = (value) => String(value).padStart(2, '0');

    if (this.units.days) this.units.days.textContent = pad(days);
    if (this.units.hours) this.units.hours.textContent = pad(hours);
    if (this.units.minutes) this.units.minutes.textContent = pad(minutes);
    if (this.units.seconds) this.units.seconds.textContent = pad(seconds);
    this.setAttribute('aria-label', `${days} days, ${hours} hours, ${minutes} minutes, ${seconds} seconds remaining`);
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}

if (!customElements.get('announcement-countdown')) {
  customElements.define('announcement-countdown', AnnouncementCountdown);
}
